import { Module } from 'vuex';
import { BOARD_CONFIGS, BoardConfig, BoardColumnConfig } from '@/config/board-config';
import { applyHardCodedRulesToStatusList, buildColumnStatusMaps } from '@/utilities/hard-coded-board-rules';

// Local storage key for board settings
const BOARD_SETTINGS_STORAGE_KEY = 'jupiter_board_settings';

/**
 * Helper function to determine if a status ID assignment is from a hard-coded rule
 * Note: This is a simplified check since we don't have access to status names here
 */
function isHardCodedAssignment(statusId: number, columnId: string): boolean {
    // Rule 1: Status ID 1 is always hard-coded to 'new'
    if (statusId === 1 && columnId === 'new') {
        return true;
    }

    // For other rules involving status names, we'd need to cache the status list
    // This is a simplified check for the most critical rule
    return false;
}

// Board settings state interface
export interface BoardSettingsState {
  boardConfigs: { [boardType: string]: BoardConfig };
  isLoaded: boolean;
  lastSaved: Date | null;
}

// Local storage structure
interface BoardSettingsLocalStorage {
  boardConfigs: { [boardType: string]: BoardConfig };
  lastSaved: string;
  version: string;
}

const boardSettingsModule: Module<BoardSettingsState, any> = {
  namespaced: true,
  
  state: (): BoardSettingsState => ({
    boardConfigs: {},
    isLoaded: false,
    lastSaved: null
  }),

  getters: {
    /**
     * Get board configuration by type
     */
    getBoardConfig: (state) => (boardType: string): BoardConfig | null => {
      return state.boardConfigs[boardType] || null;
    },

    /**
     * Get all configured board types
     */
    getAllBoardTypes: (state): string[] => {
      return Object.keys(state.boardConfigs);
    },

    /**
     * Get all status IDs that are currently assigned to any board
     */
    getAllUsedStatusIds: (state): number[] => {
      const usedIds = new Set<number>();
      Object.values(state.boardConfigs).forEach(config => {
        config.columns.forEach(column => {
          if (column.statusIds) {
            column.statusIds.forEach(id => usedIds.add(id));
          }
        });
      });
      return Array.from(usedIds);
    },

    /**
     * Get status IDs used by a specific board type
     */
    getUsedStatusIdsByBoard: (state) => (boardType: string): number[] => {
      const config = state.boardConfigs[boardType];
      if (!config) return [];
      
      const usedIds = new Set<number>();
      config.columns.forEach(column => {
        if (column.statusIds) {
          column.statusIds.forEach(id => usedIds.add(id));
        }
      });
      return Array.from(usedIds);
    },

    /**
     * Get which board and column a status ID is assigned to
     */
    getStatusAssignment: (state) => (statusId: number): { boardType: string; boardTitle: string; columnTitle: string } | null => {
      for (const [boardType, config] of Object.entries(state.boardConfigs)) {
        for (const column of config.columns) {
          if (column.statusIds && column.statusIds.includes(statusId)) {
            return {
              boardType,
              boardTitle: config.title,
              columnTitle: column.title
            };
          }
        }
      }
      return null;
    },

    /**
     * Check if settings have been loaded from storage
     */
    isLoaded: (state): boolean => state.isLoaded
  },

  mutations: {
    /**
     * Set the board configurations
     */
    SET_BOARD_CONFIGS(state, configs: { [boardType: string]: BoardConfig }) {
      state.boardConfigs = { ...configs };
    },

    /**
     * Update a specific board configuration
     */
    UPDATE_BOARD_CONFIG(state, { boardType, config }: { boardType: string; config: BoardConfig }) {
      state.boardConfigs = {
        ...state.boardConfigs,
        [boardType]: { ...config }
      };
    },

    /**
     * Update a specific column in a board
     */
    UPDATE_BOARD_COLUMN(state, { boardType, columnId, column }: { boardType: string; columnId: string; column: BoardColumnConfig }) {
      const config = state.boardConfigs[boardType];
      if (config) {
        const columnIndex = config.columns.findIndex(col => col.id === columnId);
        if (columnIndex !== -1) {
          const updatedColumns = [...config.columns];
          updatedColumns[columnIndex] = { ...column };
          
          state.boardConfigs = {
            ...state.boardConfigs,
            [boardType]: {
              ...config,
              columns: updatedColumns
            }
          };
        }
      }
    },

    /**
     * Remove status IDs from all boards before reassigning
     */
    REMOVE_STATUS_IDS_FROM_ALL_BOARDS(state, statusIds: number[]) {
      const updatedConfigs = { ...state.boardConfigs };
      
      Object.keys(updatedConfigs).forEach(boardType => {
        const config = updatedConfigs[boardType];
        const updatedColumns = config.columns.map(column => ({
          ...column,
          statusIds: column.statusIds ? column.statusIds.filter(id => !statusIds.includes(id)) : []
        }));
        
        updatedConfigs[boardType] = {
          ...config,
          columns: updatedColumns
        };
      });
      
      state.boardConfigs = updatedConfigs;
    },

    /**
     * Set loaded state
     */
    SET_LOADED(state, isLoaded: boolean) {
      state.isLoaded = isLoaded;
    },

    /**
     * Set last saved timestamp
     */
    SET_LAST_SAVED(state, timestamp: Date) {
      state.lastSaved = timestamp;
    },

    /**
     * Set default status for a column
     */
    SET_COLUMN_DEFAULT_STATUS(state, { boardType, columnId, statusId, statusName }: { boardType: string; columnId: string; statusId: number; statusName: string }) {
      const config = state.boardConfigs[boardType];
      if (config) {
        const columnIndex = config.columns.findIndex(col => col.id === columnId);
        if (columnIndex !== -1) {
          const updatedColumns = [...config.columns];
          updatedColumns[columnIndex] = {
            ...updatedColumns[columnIndex],
            defaultStatusId: statusId,
            defaultStatusName: statusName
          };

          state.boardConfigs = {
            ...state.boardConfigs,
            [boardType]: {
              ...config,
              columns: updatedColumns
            }
          };
        }
      }
    }
  },

  actions: {
    /**
     * Initialize board settings from local storage or defaults
     */
    async initializeBoardSettings({ commit, dispatch }) {
      try {
        // Try to load from local storage
        const savedSettings = localStorage.getItem(BOARD_SETTINGS_STORAGE_KEY);
        
        if (savedSettings) {
          const parsed: BoardSettingsLocalStorage = JSON.parse(savedSettings);
          
          // Validate version compatibility (future enhancement)
          if (parsed.version && parsed.boardConfigs) {
            commit('SET_BOARD_CONFIGS', parsed.boardConfigs);
            commit('SET_LAST_SAVED', new Date(parsed.lastSaved));
          } else {
            // Migration or invalid format - load defaults
            await dispatch('loadDefaultConfigs');
          }
        } else {
          // No saved settings - load defaults
          await dispatch('loadDefaultConfigs');
        }
        
        commit('SET_LOADED', true);
      } catch (error) {
        // Error loading board settings from local storage - using defaults
        await dispatch('loadDefaultConfigs');
        commit('SET_LOADED', true);
      }
    },

    /**
     * Load default board configurations with hard-coded rules
     */
      async loadDefaultConfigs({ commit, dispatch }) {
      // Start with default configurations from board-config.ts
      const defaultConfigs = { ...BOARD_CONFIGS };
      
        // Apply hard-coded rules and fetch status list for initial setup
        await dispatch('applyHardCodedRules', defaultConfigs);
      
      commit('SET_BOARD_CONFIGS', defaultConfigs);
      commit('SET_LAST_SAVED', new Date());
    },

      /**
       * Apply hard-coded rules to board configurations
       * Fetches status list and assigns statuses based on hard-coded rules
       */
      async applyHardCodedRules({ }, configs: { [boardType: string]: BoardConfig }) {
          try {
              // Import GlobalServices to fetch status list
              const GlobalServices = (await import('@/services/global')).default;
              const MODULE = (await import('@/constants/modules')).default;
              const global = new GlobalServices();

              // Fetch the project status list
              const statusOptions = await global.getUXDropDown(MODULE.PROJECTS.Status);

            // Fetched status options for board initialization

              // Apply hard-coded rules to jobs board
              if (configs.jobs) {
                  const jobsConfig = configs.jobs;

                // Use the enhanced utility function to build column status maps
                const columnStatusMaps = buildColumnStatusMaps(statusOptions);

                // Built column status maps for configuration

                  // Apply assignments to each column
                  jobsConfig.columns.forEach(column => {
                    if (columnStatusMaps[column.id]) {
                      column.statusIds = [...columnStatusMaps[column.id]];
                      // Column assigned status IDs successfully
                      } else {
                          column.statusIds = [];
                        // Column has no status IDs assigned
                      }
                  });

                // Successfully applied hard-coded rules to board configuration
              }
          } catch (error) {
            // Error applying hard-coded rules - using manual configuration
              // If fetching status list fails, just use empty arrays (manual configuration required)
          }
      },

    /**
     * Save board settings to local storage
     */
    async saveBoardSettings({ state, commit }) {
      try {
        const settingsToSave: BoardSettingsLocalStorage = {
          boardConfigs: state.boardConfigs,
          lastSaved: new Date().toISOString(),
          version: '1.0.0'
        };
        
        localStorage.setItem(BOARD_SETTINGS_STORAGE_KEY, JSON.stringify(settingsToSave));
        commit('SET_LAST_SAVED', new Date());
        
        return true;
      } catch (error) {
        // Error saving board settings to local storage
        return false;
      }
    },

    /**
     * Update column settings for a specific board
     */
    async updateColumnSettings({ commit, dispatch, getters }, { boardType, columnId, updatedColumn }: { boardType: string; columnId: string; updatedColumn: BoardColumnConfig }) {
      // First, remove the status IDs from all other boards to prevent conflicts
      if (updatedColumn.statusIds && updatedColumn.statusIds.length > 0) {
        commit('REMOVE_STATUS_IDS_FROM_ALL_BOARDS', updatedColumn.statusIds);
      }
      
      // Then update the specific column
      commit('UPDATE_BOARD_COLUMN', { boardType, columnId, column: updatedColumn });
      
      // Save to local storage
      await dispatch('saveBoardSettings');
      
      // Trigger board data refresh if needed
      await dispatch('triggerBoardRefresh', boardType);
    },

    /**
     * Set default status for a column when user opts to auto-assign future cards
     */
    async setColumnDefaultStatus({ commit, dispatch }, { boardType, columnId, statusId, statusName }: { boardType: string; columnId: string; statusId: number; statusName: string }) {
      // Update the column's default status
      commit('SET_COLUMN_DEFAULT_STATUS', { boardType, columnId, statusId, statusName });

      // Save to local storage
      await dispatch('saveBoardSettings');

      return true;
    },

    /**
     * Trigger a board refresh when settings change
     */
    async triggerBoardRefresh({ dispatch }, boardType: string) {
      // This will trigger the board store to reload data with new settings
      try {
        await dispatch('board/loadBoardData', null, { root: true });
      } catch (error) {
        // Error refreshing board data - handled by board component
      }
    },

      /**
       * Check and update hard-coded rules if status list has changed
       * This should be called periodically or when status list is suspected to have changed
       */
      async checkAndUpdateHardCodedRules({ state, commit, dispatch }) {
          try {
              // Create a temporary config to test against
              const tempConfigs = { ...state.boardConfigs };

              // Apply hard-coded rules to temporary config
              await dispatch('applyHardCodedRules', tempConfigs);

              // Check if any hard-coded assignments have changed
              let hasChanges = false;

              if (state.boardConfigs.jobs && tempConfigs.jobs) {
                  // Check each column for differences in hard-coded assignments
                  for (const column of tempConfigs.jobs.columns) {
                      const existingColumn = state.boardConfigs.jobs.columns.find(col => col.id === column.id);
                      if (existingColumn) {
                          // Check for new hard-coded statuses that should be added
                          const hardCodedIds = column.statusIds.filter(id => {
                              // Only consider IDs that would be assigned by hard-coded rules
                              return isHardCodedAssignment(id, column.id);
                          });

                          const existingHardCodedIds = existingColumn.statusIds.filter(id => {
                              return isHardCodedAssignment(id, column.id);
                          });

                          if (hardCodedIds.length !== existingHardCodedIds.length ||
                              !hardCodedIds.every(id => existingHardCodedIds.includes(id))) {
                              hasChanges = true;
                              break;
                          }
                      }
                  }
              }

              // If changes detected, update with new hard-coded rules
              if (hasChanges) {
                  commit('SET_BOARD_CONFIGS', tempConfigs);
                  await dispatch('saveBoardSettings');
                  // Board settings updated with new hard-coded rules
              }

              return hasChanges;
          } catch (error) {
              // Error checking hard-coded rules
              return false;
          }
      },

    /**
     * Reset all board settings to defaults
     */
    async resetToDefaults({ dispatch }) {
      await dispatch('loadDefaultConfigs');
      await dispatch('saveBoardSettings');
      await dispatch('triggerBoardRefresh', 'all');
    },

    /**
     * Export board settings for backup
     */
    exportSettings({ state }): string {
      const exportData = {
        boardConfigs: state.boardConfigs,
        exportedAt: new Date().toISOString(),
        version: '1.0.0'
      };
      return JSON.stringify(exportData, null, 2);
    },

    /**
     * Import board settings from backup
     */
    async importSettings({ commit, dispatch }, settingsJson: string) {
      try {
        const importData = JSON.parse(settingsJson);
        
        if (importData.boardConfigs) {
          commit('SET_BOARD_CONFIGS', importData.boardConfigs);
          await dispatch('saveBoardSettings');
          await dispatch('triggerBoardRefresh', 'all');
          return true;
        } else {
          throw new Error('Invalid settings format');
        }
      } catch (error) {
        // Error importing board settings
        return false;
      }
    }
  }
};

export default boardSettingsModule;
